import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import { isEqual } from 'lodash';
import Divider from '@material-ui/core/Divider';
import QuoteOptionModal from './QuoteOptionModal';
import { ViewOnlyQuoteAnswerList } from '../QuoteQuestionFields';
import { formatPrice } from '../../../helper';
import { apiPost } from '../../../brainApi';

const useStyles = makeStyles(() => ({
  containerPointer: {
    height: '100%',
    cursor: 'pointer',
  },
  containerNoPointer: {
    height: '100%',
    cursor: 'auto',
  },
  option: {
    padding: '10px',
    marginLeft: '20px',
    width: '300px',
    whiteSpace: 'normal',
    minHeight: '300px',
    height: '100%',
    boxSizing: 'border-box',
  },
  optionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  label: {
    marginLeft: '10px',
  },
  answersContainer: {
    marginTop: '20px',
  },
  serviceCard: {
    textAlign: 'center',
    padding: '10px',
    margin: '10px',
  },
  divider: {
    margin: '10px',
  },
}));

const QuoteOption = ({
  option,
  updateOption,
  deleteOption,
  isDragging,
  isInDropZone,
  isEditable,
}) => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(!!option.isNew);
  const [financials, setFinancials] = useState({});
  const draggingStyle = isDragging
    ? {
        boxShadow: '10px 10px 40px rgba(0, 0, 0, 0.2)',
      }
    : {};
  const dropZoneStyle =
    isDragging && !isInDropZone
      ? {
          opacity: 0.3,
        }
      : {};
  const styleOverride = { ...draggingStyle, ...dropZoneStyle };

  const openEditModal = () => (isEditable ? setIsEditing(true) : null);

  useEffect(() => {
    if (option.isNew) updateOption('isNew', false);
  }, []);

  // When services change, recalculate financials
  // This calculation was triggering too often, so I compare services to its previous value manually
  // https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
  const prevServices = useRef();
  useEffect(() => {
    if (!isEqual(prevServices.current, option.services)) {
      apiPost('/scheduled-work/cost-preview', {
        services_data: JSON.stringify(option.services),
      }).then(({ Data: financials }) => {
        setFinancials(financials);
      });
      prevServices.current = option.services;
    }
  }, [option.services]);

  return (
    <Grid
      className={
        isEditable ? classes.containerPointer : classes.containerNoPointer
      }
    >
      <Tooltip
        title={isEditable ? 'Click to edit. Drag to reorder.' : ''}
        placement="top"
      >
        <Paper
          className={classes.option}
          style={styleOverride}
          onClick={openEditModal}
        >
          <Grid className={classes.optionHeader}>
            <Typography variant="h3">
              {option.name || `Option ${option.optionIndex + 1}`}
            </Typography>
            <Typography variant="h6" className={classes.label}>
              {option.label && option.label.toUpperCase()}
            </Typography>
          </Grid>
          <Grid>
            <Typography variant="p">{option.note}</Typography>
          </Grid>
          <Grid className={classes.answersContainer}>
            <ViewOnlyQuoteAnswerList answers={option.answers} />
          </Grid>
          <Divider variant="middle" className={classes.divider} />
          <Grid>
            {option.services.map(
              ({ display_name, parts_cost_dollars, labor_cost_dollars }) => (
                <Card raised className={classes.serviceCard} variant="outlined">
                  <Typography variant="body1">
                    <strong>{`${display_name}: ${formatPrice(
                      labor_cost_dollars + parts_cost_dollars,
                    )}`}</strong>
                  </Typography>
                </Card>
              ),
            )}
          </Grid>
          {option.services?.length > 0 ? (
            <>
              <Divider variant="middle" className={classes.divider} />
              <Grid container direction="column" alignItems="flex-end">
                <Typography>
                  Subtotal: {formatPrice(financials.subtotal_dollars)}
                </Typography>
                <Typography>
                  Shop Fee: {formatPrice(financials.shop_fee_dollars)}
                </Typography>
                <Typography>
                  <strong>
                    Total (excluding tax):{' '}
                    {formatPrice(financials.total_amount_dollars)}
                  </strong>
                </Typography>
              </Grid>
            </>
          ) : (
            <Grid zeroMinWidth>
              <Typography variant="p" style={{ color: 'lightgrey' }}>
                Note: Quote option must have at least 1 service attached before
                sending quote
              </Typography>
            </Grid>
          )}
        </Paper>
      </Tooltip>
      {isEditing && (
        <QuoteOptionModal
          option={option}
          updateOption={updateOption}
          deleteOption={deleteOption}
          onClose={() => setIsEditing(false)}
        />
      )}
    </Grid>
  );
};

export default QuoteOption;
